import * as React from "react";
import * as ReactDOM from "react-dom/client";
import {SessionProvider} from "./contexts/SessionContext";
import {MonsterCatalogProvider} from "./contexts/MonsterCatalogContext";
import {
    createBrowserRouter,
    RouterProvider,
} from "react-router-dom";
import Home from "./Home";
import CheckIn from "./CheckIn";
import Donation from "./Donation";
import LogisticsGrid from "./components/LogisticsGrid";
import LogisticsEventLanding from "./logistics/LogisticsEventLanding";
import Registration, {registrationListLoader, registrationLoader} from "./Registration";
import ListUpload from "./ListUpload";
import Maps from "./Maps";
import PreGame from "./pregame/PreGame";
import SheetsUpload from "./SheetsUpload";
import CabinAssignments, {cabinsLoader} from "./pregame/CabinAssignments";
import Tags from "./pregame/Tags";
import Payments, {paymentsLoader} from "./pregame/Payments";
import Stamps from "./GsSpend";
import OutstandingItems, {outstandingLoader} from "./pregame/Outstanding";
import SystemAdmin from "./admin/SystemAdmin";
import ChapterManager from "./admin/ChapterManager";
import MonsterAbilityManager from "./admin/MonsterAbilityManager";
import AccessControl from "./admin/AccessControl";
import EventManagement from "./admin/EventManagement";
import LandingGrid from "./components/LandingGrid";
import {PlotList, plotListLoader, plotLoader, PlotViewer} from "./Plot";
import {EventViewer, eventLoader} from "./EventViewer";
import {npcLoader, NPCViewer} from "./NPC";
import {NPCCreate} from "./NPCCreate";
import {NPCList} from "./components/NPCList";
import {monsterListLoader, MonsterLanding} from "./MonsterLanding";
import {monsterLoader, MonsterViewer} from "./MonsterViewer";
import {monsterTypeLoader, MonsterTypeViewer} from "./MonsterTypeViewer";
import {monsterSpeciesLoader, MonsterSpeciesViewer} from "./MonsterSpeciesViewer";
import UserSettings from "./UserSettings";
import {ItemList, itemListLoader} from "./ItemList";
import {ItemViewer, itemLoader} from "./ItemViewer";
import {MagicItemList, magicItemListLoader} from "./MagicItemList";
import {MagicItemViewer, magicItemLoader} from "./MagicItemViewer";
import MagicItemAdminPanel from "./admin/MagicItemAdminPanel";
import TpItemsAdminPanel from "./admin/TpItemsAdminPanel";
import EventSiteManagement from "./admin/EventSiteManagement";
import {TreasurePolicy, treasurePolicyLoader} from "./TreasurePolicy";
import {TreasurePolicyEvent, treasurePolicyEventLoader} from "./TreasurePolicyEvent";


const router = createBrowserRouter([
    {
        path: "/",
        element: <Home/>,
        children: [
            {index: true, element: <LandingGrid/>},
            {path: "settings", element: <UserSettings/>},
            {
                path: "plot",
                handle: {crumb: () => ({label: 'Plots', to: '/plot'})},
                children: [
                    {index: true, element: <PlotList/>, loader: plotListLoader},
                    {
                        path: "events/:id",
                        element: <EventViewer/>,
                        loader: eventLoader,
                        handle: {crumb: (data: any) => ({label: data?.name ?? 'Event', to: null})}
                    },
                    {
                        path: ":id",
                        element: <PlotViewer/>,
                        loader: plotLoader,
                        handle: {crumb: (data: any) => ({label: data?.name ?? 'Plot', to: null})}
                    }
                ]
            },
            {
                path: "npc",
                handle: {crumb: () => ({label: 'NPCs', to: '/npc'})},
                children: [
                    {index: true, element: <NPCList/>},
                    {
                        path: "new",
                        element: <NPCCreate/>,
                        handle: {crumb: () => ({label: 'New NPC', to: null})}
                    },
                    {
                        path: ":id",
                        element: <NPCViewer/>,
                        loader: npcLoader,
                        handle: {
                            crumb: (data: any) => [
                                {label: data?.type === 'FACEROLE' ? 'Face Role' : 'Named NPC', to: null},
                                {label: data?.name ?? 'NPC', to: null}
                            ]
                        }
                    }
                ]
            },
            {
                path: "monsters",
                handle: {crumb: () => ({label: 'Monsters', to: '/monsters'})},
                children: [
                    {index: true, element: <MonsterLanding/>, loader: monsterListLoader},
                    {
                        path: "species/:id",
                        element: <MonsterSpeciesViewer/>,
                        loader: monsterSpeciesLoader,
                        handle: {crumb: (data: any) => ({label: data?.name ?? 'Species', to: null})}
                    },
                    {
                        path: "type/:id",
                        element: <MonsterTypeViewer/>,
                        loader: monsterTypeLoader,
                        handle: {
                            crumb: (data: any) => [
                                {label: data?.species?.name ?? 'Species', to: data?.species?.id != null ? `/monsters/species/${data.species.id}` : null},
                                {label: data?.name ?? 'Type', to: null}
                            ]
                        }
                    },
                    {
                        path: ":id",
                        element: <MonsterViewer/>,
                        loader: monsterLoader,
                        handle: {
                            crumb: (data: any) => [
                                {label: data?.type?.species?.name ?? 'Species', to: data?.type?.species?.id != null ? `/monsters/species/${data.type.species.id}` : null},
                                {label: data?.type?.name ?? 'Type', to: data?.type?.id != null ? `/monsters/type/${data.type.id}` : null},
                                {label: data?.name || 'New Monster', to: null}
                            ]
                        }
                    }
                ]
            },
            {
                path: "magic-items",
                handle: {crumb: () => ({label: 'Magic Item Manager', to: '/magic-items'})},
                children: [
                    {index: true, element: <MagicItemList/>, loader: magicItemListLoader},
                    {
                        path: "new",
                        element: <MagicItemViewer/>,
                        handle: {crumb: () => ({label: 'New Magic Item', to: null})}
                    },
                    {
                        path: ":id",
                        element: <MagicItemViewer/>,
                        loader: magicItemLoader,
                        handle: {crumb: (data: any) => ({label: data?.name ?? 'Magic Item', to: null})}
                    }
                ]
            },
            {
                path: "treasure-policy",
                handle: {crumb: () => ({label: 'Treasure Policy', to: '/treasure-policy'})},
                children: [
                    {index: true, element: <TreasurePolicy/>, loader: treasurePolicyLoader},
                    {
                        path: ":id",
                        element: <TreasurePolicyEvent/>,
                        loader: treasurePolicyEventLoader,
                        handle: {crumb: (data: any) => ({label: data?.event?.name ?? 'Event', to: null})}
                    }
                ]
            },
            {
                path: "items",
                handle: {crumb: () => ({label: 'Item Manager', to: '/items'})},
                children: [
                    {index: true, element: <ItemList/>, loader: itemListLoader},
                    {
                        path: "new",
                        element: <ItemViewer/>,
                        handle: {crumb: () => ({label: 'New Item', to: null})}
                    },
                    {
                        path: ":id",
                        element: <ItemViewer/>,
                        loader: itemLoader,
                        handle: {crumb: (data: any) => ({label: data?.name ?? 'Item', to: null})}
                    }
                ]
            },
            {
                path: "admin",
                handle: {crumb: () => ({label: 'Admin', to: '/admin'})},
                children: [
                    {index: true, element: <SystemAdmin/>},
                    {
                        path: "chapter",
                        element: <ChapterManager/>,
                        handle: {crumb: () => ({label: 'Chapter Manager', to: null})}
                    },
                    {
                        path: "access_control",
                        element: <AccessControl/>,
                        handle: {crumb: () => ({label: 'Access Control', to: null})}
                    },
                    {
                        path: "events",
                        element: <EventManagement/>,
                        handle: {crumb: () => ({label: 'Events', to: null})}
                    },
                    {
                        path: "monster-abilities",
                        element: <MonsterAbilityManager/>,
                        handle: {crumb: () => ({label: 'Monster Ability Management', to: null})}
                    },
                    {
                        path: "magic-item-admin",
                        element: <MagicItemAdminPanel/>,
                        handle: {crumb: () => ({label: 'Magic Item Management', to: null})}
                    },
                    {
                        path: "tp-items",
                        element: <TpItemsAdminPanel/>,
                        handle: {crumb: () => ({label: 'TP Item Management', to: null})}
                    },
                    {
                        path: "event-sites",
                        element: <EventSiteManagement/>,
                        handle: {crumb: () => ({label: 'Event Site Management', to: null})}
                    }
                ]
            },
            {
                path: "logistics",
                handle: {crumb: () => ({label: 'Logistics', to: '/logistics'})},
                children: [
                    {index: true, element: <LogisticsEventLanding/>},
                    {
                        path: ":eventId",
                        children: [
                            {index: true, element: <LogisticsGrid/>},
                            {
                                path: "pcs",
                                handle: {crumb: () => ({label: 'Check-In', to: null})},
                                children: [{
                                    index: true,
                                    element: <CheckIn/>,
                                    loader: registrationListLoader,
                                }, {
                                    path: ":id",
                                    element: <Registration/>,
                                    loader: registrationLoader,
                                    handle: {crumb: (data: any) => ({label: data?.name ?? 'Registration', to: null})}
                                }]
                            },
                            {
                                path: "goblin-stamps",
                                element: <Stamps/>,
                                loader: registrationListLoader,
                                handle: {crumb: () => ({label: 'Goblin Stamps', to: null})}
                            },
                            {
                                path: "donations",
                                element: <Donation/>,
                                loader: registrationListLoader,
                                handle: {crumb: () => ({label: 'Donations', to: null})}
                            },
                            {
                                path: "maps",
                                element: <Maps/>,
                                handle: {crumb: () => ({label: 'Maps', to: null})}
                            },
                            {
                                path: "questions",
                                element: <ListUpload/>
                            },
                            {
                                path: "sheets",
                                element: <SheetsUpload/>
                            },
                            {
                                path: "pregame",
                                handle: {crumb: () => ({label: 'Pre-Game', to: null})},
                                children: [
                                    {index: true, element: <PreGame/>},
                                    {
                                        path: "cabins",
                                        element: <CabinAssignments/>,
                                        loader: cabinsLoader,
                                        handle: {crumb: () => ({label: 'Cabin Assignments', to: null})}
                                    },
                                    {
                                        path: "tags",
                                        element: <Tags/>,
                                        loader: registrationListLoader,
                                        handle: {crumb: () => ({label: 'Tags', to: null})}
                                    },
                                    {
                                        path: "payments",
                                        element: <Payments/>,
                                        loader: paymentsLoader,
                                        handle: {crumb: () => ({label: 'Payments', to: null})}
                                    },
                                    {
                                        path: "outstanding",
                                        element: <OutstandingItems/>,
                                        loader: outstandingLoader,
                                        handle: {crumb: () => ({label: 'Outstanding Items', to: null})}
                                    }
                                ]
                            }
                        ]
                    }
                ]
            }
        ]
    }]);

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

root.render(
    <React.StrictMode>
        <SessionProvider>
            <MonsterCatalogProvider>
                <RouterProvider router={router}/>
            </MonsterCatalogProvider>
        </SessionProvider>
    </React.StrictMode>
);
